import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import ComputerIcon from "@mui/icons-material/Computer";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import RouterIcon from "@mui/icons-material/Router";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import DnsIcon from "@mui/icons-material/Dns";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import { formatDate as formatDateUtil, formatTimestampParts } from "../../utils/dateUtils";
import { CheckStatusChip } from "../../components/CheckStatusChip/CheckStatusChip";
import {
  CheckStatus,
  AssetInactivityReason,
  ASSET_OUT_OF_SCOPE_EFFECT,
} from "../../utils/enums";

// Compliance scope is disposal-driven: only a disposed device leaves the scope.
// A logged-out one stays in it — it still counts, it just isn't reporting right
// now. Note this is narrower than the backend's `currentStatus`, which is
// "inactive" for logged-out devices too.
export const isAssetInScope = (row) => {
  // An admin marking the device out of scope is authoritative and independent
  // of disposal, so it is checked first. `inScope` is only absent on responses
  // predating the field; undefined must not read as out of scope.
  if (row?.inScope === false) return false;
  const reason = String(row?.inactivityReason ?? "").toLowerCase();
  if (reason) {
    return (
      reason !== AssetInactivityReason.DISPOSED &&
      reason !== AssetInactivityReason.OUT_OF_SCOPE
    );
  }
  // Rows predating `inactivityReason`: fall back to the legacy status, then to
  // the disposal date itself. Disposal takes effect on the date, not when it is
  // scheduled, matching computeActivity on the API side.
  if (String(row?.status ?? "").toLowerCase() === "disposed") return false;
  return !isAssetDisposed(row);
};

// The disposal date has been reached, so the device is out of scope regardless
// of its admin `inScope` flag. Kept separate from isAssetInScope because the
// details dialog has to distinguish "an admin excluded this" from "this is
// disposed" — only the former can be undone with the scope toggle.
export const isAssetDisposed = (row) => {
  const reason = String(row?.inactivityReason ?? "").toLowerCase();
  if (reason === AssetInactivityReason.DISPOSED) return true;
  if (!row?.disposableDate) return false;
  const disposedAt = new Date(row.disposableDate).getTime();
  return !Number.isNaN(disposedAt) && disposedAt <= Date.now();
};

// The agent session ended, so the device has stopped syncing — its checks hold
// the last values reported before logout. `inactivityReason` wins when present,
// so a device that is both disposed and logged out reads as disposed.
export const isAssetLoggedOut = (row) => {
  const reason = String(row?.inactivityReason ?? "").toLowerCase();
  if (reason) return reason === AssetInactivityReason.LOGGED_OUT;
  return String(row?.status ?? "").toUpperCase() === "LOGGED_OUT";
};

// Device type is backend-sourced (row.deviceType). Maps it to an icon; falls
// back to a generic computer icon until the field is present. Reads a few
// common field names so it works whatever the API ends up calling it.
const getDeviceIcon = (row) => {
  const type = String(row.deviceType || row.type || row.formFactor || "").toLowerCase();
  switch (type) {
    case "laptop":
    case "notebook":
    case "macbook":
      return LaptopMacIcon;
    case "desktop":
    case "pc":
    case "workstation":
    case "imac":
      return DesktopWindowsIcon;
    case "router":
    case "gateway":
    case "switch":
    case "firewall":
    case "access_point":
      return RouterIcon;
    case "phone":
    case "mobile":
    case "smartphone":
    case "iphone":
    case "android":
      return SmartphoneIcon;
    case "tablet":
    case "ipad":
      return TabletMacIcon;
    case "server":
      return DnsIcon;
    case "":
      return ComputerIcon;
    default:
      return DevicesOtherIcon;
  }
};

const sourceLabel = (source, integrationName) =>
  source === "WATCH_TOWER" ? "WatchTower" : integrationName || "MDM";

export const groupAssetsBySerial = (rows) => rows;

export const formatActivity = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  if (mins < 10080) return `${Math.round(mins / 1440)}d ago`;
  return formatDateUtil(iso);
};

// Maps each check column field to its checkType + legacy boolean fallback
export const SECURITY_CHECKS = [
  {
    field: "diskEnc",
    checkType: "disk_encryption_enabled",
    pass: (r) => r.diskEncryption === true,
    fail: (r) => r.diskEncryption === false,
  },
  {
    field: "antivirus",
    checkType: "antivirus_installed",
    pass: (r) => r.edrInstalled === true,
    fail: (r) => r.edrInstalled === false,
  },
  {
    field: "firewall",
    checkType: "firewall_enabled",
    pass: (r) => r.firewallEnabled === true,
    fail: (r) => r.firewallEnabled === false,
  },
  {
    field: "autoUpdate",
    checkType: "auto_update_enabled",
    pass: (r) => r.patchStatus === "up_to_date",
    fail: (r) => r.patchStatus != null && r.patchStatus !== "up_to_date",
  },
  {
    field: "screenLock",
    checkType: "screen_timeout_enabled",
    pass: (r) => r.screenLockEnabled === true,
    fail: (r) => r.screenLockEnabled === false,
  },
];

// Returns "pass" | "fail" | "pending" for a check on an asset row.
// Prefers embedded checkStatus.checks; falls back to legacy flat boolean fields.
export const getCheckStatus = (row, chk) => {
  if (row.checkStatus?.checks?.length) {
    const found = row.checkStatus.checks.find((c) => c.checkType === chk.checkType);
    if (found) return found.status;
  }
  if (chk.pass(row)) return CheckStatus.PASS;
  if (chk.fail(row)) return CheckStatus.FAIL;
  return CheckStatus.PENDING;
};

const getManagedLabels = (row) => {
  if (row.integrationName) return [row.integrationName];
  if (row.source === "WATCH_TOWER") return ["Rego WatchTower"];
  return ["—"];
};

const CHECK_STATUS_OPTIONS = [
  { id: "pass",    label: "Passing" },
  { id: "fail",    label: "Failing" },
  { id: "pending", label: "Pending" },
];

const checkHeader = (Icon, name) => (
  <Tooltip title={name} arrow placement="top">
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      <Icon sx={{ fontSize: 15, opacity: 0.8, verticalAlign: "middle" }} />
    </Box>
  </Tooltip>
);

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

// Columns declare a relative `weight` sized to the content they hold, and the
// set is scaled to fill exactly what the caller leaves over. Two things this
// avoids:
//   - Hard-coded percentages, which drifted once columns became conditional:
//     totals ran from 68% (employee, Out of Scope — five check columns hidden)
//     to 101% (admin, In Scope), and anything unallocated went to the browser's
//     table algorithm, which spreads it by content and looks uneven.
//   - Equal widths, which are even but wrong for this data: Device carries a
//     name over a serial number and needs roughly three times what a status chip
//     does, so splitting evenly truncates device names to widen chip columns
//     that were already roomy.
const scaleToFit = (columns, reservedWidth) => {
  const totalWeight = columns.reduce((sum, c) => sum + c.weight, 0);
  const available = Math.max(0, 100 - reservedWidth);
  return columns.map(({ weight, ...col }) => ({
    ...col,
    width: totalWeight ? `${((weight / totalWeight) * available).toFixed(2)}%` : undefined,
  }));
};

/**
 * `reservedWidth` is the percentage the caller spends on columns it appends
 * itself — the admin checkbox and the row action arrow. Pass it so the scaled
 * columns and those extras add up to 100% instead of overflowing or leaving a gap.
 */
export const buildAssetColumns = ({
  showOwner = true,
  showChecks = true,
  ownerOptions = [],
  sourceOptions = [],
  reservedWidth = 0,
} = {}) =>
  scaleToFit(
    [
      // Two lines of content (name over serial), so it needs the most room.
      { label: "Device", field: "device", weight: 24, align: "left" },
      showOwner && {
        label: "Owner",
        field: "owner",
        weight: 15,
        align: "left",
        filterConfig: { filterOptions: ownerOptions },
      },
      // A "Passing"/"Failing"/"Disabled" chip and nothing else. Narrow, but not
      // as narrow as it looks: the chip label ellipsises to "Passin…" once the
      // text area drops below ~46px, which on the admin layout (nine columns
      // plus a checkbox) happens on any laptop-width screen. Weight 10 keeps it
      // clear of that on a ~1000px table.
      showChecks && { label: checkHeader(LockOutlinedIcon,          "Disk Encryption"), field: "diskEnc",    weight: 10, align: "center", filterConfig: { filterOptions: CHECK_STATUS_OPTIONS } },
      showChecks && { label: checkHeader(SecurityOutlinedIcon,       "Antivirus"),       field: "antivirus",  weight: 10, align: "center", filterConfig: { filterOptions: CHECK_STATUS_OPTIONS } },
      showChecks && { label: checkHeader(GppGoodOutlinedIcon,        "Firewall"),        field: "firewall",   weight: 10, align: "center", filterConfig: { filterOptions: CHECK_STATUS_OPTIONS } },
      showChecks && { label: checkHeader(SystemUpdateAltOutlinedIcon,"Auto Update"),     field: "autoUpdate", weight: 10, align: "center", filterConfig: { filterOptions: CHECK_STATUS_OPTIONS } },
      showChecks && { label: checkHeader(LockClockOutlinedIcon,      "Screen Lock"),     field: "screenLock", weight: 10, align: "center", filterConfig: { filterOptions: CHECK_STATUS_OPTIONS } },
      {
        label: "Source",
        field: "managed",
        weight: 15,
        align: "left",
        filterConfig: { filterOptions: sourceOptions },
      },
      { label: "Last Sync", field: "activity", weight: 12, align: "left", sortable: true },
    ].filter(Boolean),
    reservedWidth
  );

// ---------------------------------------------------------------------------
// Cell renderer
// ---------------------------------------------------------------------------
export const makeRenderAssetCell = (theme, { timeZone = "Local" } = {}) => (row, column) => {
  switch (column.field) {

    case "device": {
      const DeviceIcon = getDeviceIcon(row);
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Tooltip title={row.deviceType || row.type || ""} arrow>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                backgroundColor: theme.customColors.background.light,
                color: theme.customColors.icons.dark,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DeviceIcon fontSize="small" />
            </Box>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Tooltip title={row.name || row.hostname || ""} arrow>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ color: theme.customColors.text.strong, minWidth: 0 }}>
                {row.name || row.hostname || "—"}
              </Typography>
            </Tooltip>
            <Typography variant="body2" noWrap sx={{ color: theme.customColors.text.secondary }}>
              {row.serialNumber || "—"}
            </Typography>
          </Box>
        </Box>
      );
    }

    case "owner":
      return (
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={row.ownerEmail || ""} arrow>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: theme.customColors.text.strong }}>
              {row.ownerName || row.ownerEmail || "—"}
            </Typography>
          </Tooltip>
          {row.ownerEmail && row.ownerName && (
            <Typography variant="body2" noWrap sx={{ color: theme.customColors.text.secondary }}>
              {row.ownerEmail}
            </Typography>
          )}
        </Box>
      );

    case "diskEnc":
    case "antivirus":
    case "firewall":
    case "autoUpdate":
    case "screenLock": {
      // Out-of-scope assets aren't monitored, so their checks are meaningless — hide them.
      if (!isAssetInScope(row)) {
        // A bare dash says nothing about why the check is blank. Same sentence
        // the details dialog and the scope toggle use.
        return (
          <Tooltip title={ASSET_OUT_OF_SCOPE_EFFECT} arrow>
            <Typography variant="body2" sx={{ color: theme.customColors.text.secondary, cursor: "help" }}>
              —
            </Typography>
          </Tooltip>
        );
      }
      const chk = SECURITY_CHECKS.find((c) => c.field === column.field);
      const status = getCheckStatus(row, chk);
      return <CheckStatusChip status={status} dense />;
    }

    case "managed": {
      const label = getManagedLabels(row)[0];
      return label
        ? (
          <Tooltip title={label} arrow>
            <Typography variant="body2" noWrap sx={{ color: theme.customColors.text.strong, fontSize: "0.8rem" }}>{label}</Typography>
          </Tooltip>
        )
        : <Typography variant="body2" sx={{ color: theme.customColors.text.secondary }}>—</Typography>;
    }

    case "activity": {
      // Table shows the date only; the full date + time lives in the details dialog.
      const { date, full } = formatTimestampParts(row.lastActivityAt, timeZone);
      // A logged-out device has stopped syncing, so the cell says so rather than
      // showing a timestamp that only keeps aging; the last sync stays in the tooltip.
      if (isAssetLoggedOut(row)) {
        return (
          <Tooltip title={full ? `Last synced ${full}` : ""} arrow>
            <Typography
              variant="body2"
              noWrap
              sx={{ color: theme.customColors.text.secondary, fontWeight: 500 }}
            >
              Logged out
            </Typography>
          </Tooltip>
        );
      }
      return date ? (
        <Tooltip title={full} arrow>
          <Typography
            variant="body2"
            noWrap
            sx={{ color: theme.customColors.text.strong, fontWeight: 500 }}
          >
            {date}
          </Typography>
        </Tooltip>
      ) : (
        <Typography
          variant="body2"
          noWrap
          sx={{ color: theme.customColors.text.secondary, fontWeight: 500 }}
        >
          Never
        </Typography>
      );
    }

    default:
      return row[column.field] ?? "—";
  }
};

// ---------------------------------------------------------------------------
export const filterAssets = (rows, q) => {
  if (!q.trim()) return rows;
  const needle = q.trim().toLowerCase();
  return rows.filter((r) =>
    [r.name, r.hostname, r.model, r.serialNumber, r.platform, r.osName, r.ownerName, r.ownerEmail]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(needle))
  );
};
